import { useEffect, useState } from "react";
import { Button, Modal, Spin, message } from "antd";
import { CheckCircleFilled, CloseOutlined, ExclamationOutlined } from "@ant-design/icons";
import YextApi, { YextListing, YextListingsView, YextListingStatus, yextErrorMessage } from "../../api/YextApi";
import { PublisherLogo } from "./PublisherLogo";
import "./ListingsListingsTab.scss";

interface Props {
  locationId?: number;
}

type ChipClass = "verified" | "warning" | "danger";

// Maps a normalized scan status to a display chip (icon + label + severity class).
function statusChip(status: YextListingStatus): { cls: ChipClass; label: string } {
  switch (status) {
    case "LIVE":
      return { cls: "verified", label: "Live" };
    case "NOT_VERIFIED":
      return { cls: "warning", label: "Not Verified Through Yext" };
    case "WRONG_PHONE":
      return { cls: "danger", label: "Wrong Phone Number" };
    case "INCORRECT_INFO":
      return { cls: "danger", label: "Incorrect Information" };
    case "OPTED_OUT":
      return { cls: "danger", label: "Not Published" };
    case "PROCESSING":
      return { cls: "warning", label: "Processing" };
    case "NEEDS_CONNECTION":
      return { cls: "warning", label: "Connect account" };
    case "NOT_FOUND":
    default:
      return { cls: "danger", label: "Not Found" };
  }
}

// Whether a row counts as an outstanding issue (anything not Live or still processing).
function isIssue(status: YextListingStatus): boolean {
  return status !== "LIVE" && status !== "PROCESSING";
}

export function ListingsListingsTab({ locationId }: Props) {
  const [view, setView] = useState<YextListingsView | null>(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);

  async function load() {
    if (!locationId) return;
    setLoading(true);
    try {
      const { data } = await YextApi.getListings(locationId);
      setView(data);
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to load listings."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  async function fixAll() {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    setFixing(true);
    try {
      const { data } = await YextApi.publish(locationId);
      message.success(`Syncing your info to ${data.publisherCount} directories — statuses will update as they sync.`);
      await load();
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to sync listings."));
    } finally {
      setFixing(false);
    }
  }

  // Owned publishers (Google/Facebook) need a one-time customer account link before they can sync.
  // That handoff is a Yext-platform/partner flow with no public API to embed, so for now we explain
  // the step. Swap this for the real connect handoff once the Yext white-label mechanism is wired.
  function connect(row: YextListing) {
    Modal.info({
      title: `Connect your ${row.publisherName} account`,
      content:
        `${row.publisherName} requires you to verify ownership before we can publish your business and ` +
        `menu there. This is a one-time step that grants permission to manage your ${row.publisherName} ` +
        `listing. We'll guide you through connecting it shortly.`,
      okText: "Got it",
    });
  }

  const listings = view?.listings ?? [];
  const issueCount = listings.filter((l) => isIssue(l.status)).length;
  const hasIssues = issueCount > 0;

  return (
    <div className="listings-tab-listings">
      <div className="lll-status-bar">
        <div className="lll-status-summary">
          {listings.length > 0 &&
            (hasIssues ? (
              <span className="lll-issues-pill">
                <span className="lll-issues-dot">
                  <ExclamationOutlined />
                </span>
                <span>Issues detected</span>
              </span>
            ) : (
              <span className="lll-issues-pill lll-issues-ok">
                <span className="lll-ok-dot">
                  <CheckCircleFilled />
                </span>
                <span>Listings look good</span>
              </span>
            ))}
        </div>
        {listings.length > 0 && (
          <Button type="primary" className="lll-fix-all-btn" onClick={fixAll} loading={fixing}>
            {hasIssues ? "Fix all errors" : "Re-sync"}
          </Button>
        )}
      </div>

      {loading && !view ? (
        <div className="lll-empty">
          <Spin />
        </div>
      ) : listings.length === 0 ? (
        <div className="lll-empty">Not published to any directories yet. Publish your profile to start a scan.</div>
      ) : (
        <div className="lll-table">
          <div className="lll-thead">
            <div className="lll-th lll-col-publisher" />
            <div className="lll-th lll-col-name">Business Name</div>
            <div className="lll-th lll-col-address">Address</div>
            <div className="lll-th lll-col-phone">Phone</div>
            <div className="lll-th lll-col-status">Status</div>
          </div>
          <div className="lll-tbody">
            {listings.map((row) => (
              <ListingRow key={row.publisherId} row={row} view={view!} onConnect={connect} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ListingRow({
  row,
  view,
  onConnect,
}: {
  row: YextListing;
  view: YextListingsView;
  onConnect: (row: YextListing) => void;
}) {
  const chip = statusChip(row.status);
  const missing = row.status === "NOT_FOUND";
  const processing = row.status === "PROCESSING";
  const needsConnection = row.status === "NEEDS_CONNECTION";

  return (
    <div className="lll-row">
      <div className="lll-td lll-col-publisher">
        <PublisherLogo publisher={row.publisherName || row.publisherId} />
        <div className="lll-publisher-text">
          <div className="lll-publisher-name">{row.publisherName}</div>
          {row.listingUrl && (
            <a className="lll-view-listing-link" href={row.listingUrl} target="_blank" rel="noreferrer">
              View Listing
            </a>
          )}
        </div>
      </div>

      {processing ? (
        <div className="lll-td lll-col-loading-cell">LOADING...</div>
      ) : missing ? (
        <div className="lll-td lll-col-missing">MISSING LISTING</div>
      ) : (
        <>
          <div className="lll-td lll-col-name">{view.businessName || "—"}</div>
          <div className="lll-td lll-col-address">{view.address || "—"}</div>
          <div className="lll-td lll-col-phone">{view.phone || "—"}</div>
        </>
      )}

      <div className="lll-td lll-col-status">
        <span className={`lll-status-chip ${chip.cls}`} title={row.message || undefined}>
          {chip.cls === "verified" ? (
            <CheckCircleFilled className="lll-chip-check" />
          ) : (
            <span className="lll-chip-icon">
              {chip.cls === "danger" ? <CloseOutlined /> : <ExclamationOutlined />}
            </span>
          )}
          <span>{chip.label}</span>
        </span>
        {needsConnection && (
          <Button type="link" className="lll-connect-btn" onClick={() => onConnect(row)}>
            Connect {row.publisherName}
          </Button>
        )}
      </div>
    </div>
  );
}
